import {
  HttpClient,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { SECURITY_API_URL, APP_CONFIG, ROUTES } from '@shared/config';
import {
  OrCheckLoginModel,
  OrLoginModel,
  OrLoginResponse,
} from '@shared/models/login/or-login.model';
import { map, Observable, of, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { catchError, finalize, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { GrantType } from './models/security-api-token.model';
import { HttpErrorSkip } from '../interceptors/http-error/http-error-skip.model';
import { LoginErrorType } from '../should-login/should-login.config';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { INTERNAL_TOKEN_LIFETIME_MS } from '@workers/keep-alive/keep-alive.config';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly authApiUrl: string = inject(APP_CONFIG).securityApiUrl;
  private readonly appCode: string = inject(APP_CONFIG).appCode;
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private _accessTokenExpirationMs = signal<number>(0);
  public accessTokenExpirationMs = this._accessTokenExpirationMs.asReadonly();
  private authenticateInFlight$: Observable<OrLoginModel> | null = null;

  public authenticate(idToken: string): Observable<OrLoginModel> {
    if (this.authenticateInFlight$) {
      return this.authenticateInFlight$;
    }

    this.authenticateInFlight$ = this.http
      .get<OrLoginResponse>(`${this.authApiUrl}${SECURITY_API_URL.auth}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        withCredentials: true,
        params: {
          grant_type: GrantType.MSAL,
        },
      })
      .pipe(
        tap(response => this.setExpirationDate(response.expiresIn)),
        map((response: OrLoginResponse) =>
          this.mapJWTToOrLoginModel(response.accessToken)
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          this.authenticateInFlight$ = null;
        })
      );

    return this.authenticateInFlight$;
  }

  public logout(): Observable<void> {
    return this.http.post<void>(
      `${this.authApiUrl}${SECURITY_API_URL.logout}`,
      {},
      {
        withCredentials: true,
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [
              HttpStatusCode.Unauthorized,
              HttpStatusCode.BadRequest,
            ],
          }).parseToString(),
        },
      }
    );
  }

  public status(): Observable<OrCheckLoginModel> {
    return this.http
      .get<OrCheckLoginModel>(`${this.authApiUrl}${SECURITY_API_URL.status}`, {
        withCredentials: true,
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [HttpStatusCode.Unauthorized],
          }).parseToString(),
        },
      })
      .pipe(
        catchError(
          (error: HttpErrorResponse): Observable<OrCheckLoginModel> => {
            console.error(error);
            return of({
              isLoggedIn: false,
            });
          }
        )
      );
  }

  public refreshToken(): Observable<void> {
    return this.http
      .post<OrLoginResponse>(
        `${this.authApiUrl}${SECURITY_API_URL.refresh}`,
        {},
        {
          withCredentials: true,
          headers: {
            [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
              skipStatusCodes: [
                HttpStatusCode.Unauthorized,
                HttpStatusCode.BadRequest,
              ],
            }).parseToString(),
          },
        }
      )
      .pipe(
        tap(response => this.setExpirationDate(response.expiresIn)),
        map(() => undefined)
      );
  }

  public forceLogin() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([ROUTES.home]);
    });
  }

  public verifyAppAccess(): boolean {
    const applications = LocalStorageService.getApplications();
    if (!applications || applications.length === 0) {
      this.forceLogin();
      return false;
    }

    if (!applications.includes(this.appCode)) {
      this.router.navigate(['/should-login'], {
        queryParams: {
          err: LoginErrorType.FORBIDDEN_APP_ACCESS,
        },
      });
      return false;
    }

    return true;
  }

  private mapJWTToOrLoginModel(token: string): OrLoginModel {
    const decoded = jwtDecode<{
      user: string;
      authorities: unknown;
      groups: unknown;
      applications: unknown;
    }>(token);

    return {
      userEmail: decoded.user,
      authorities: JSON.stringify(decoded.authorities),
      groups: JSON.stringify(decoded.groups),
      applications: JSON.stringify(decoded.applications),
    };
  }

  private setExpirationDate(expiresInSeconds: number) {
    const normalizedExpiry =
      expiresInSeconds && !Number.isNaN(expiresInSeconds)
        ? expiresInSeconds * 1000
        : INTERNAL_TOKEN_LIFETIME_MS;
    this._accessTokenExpirationMs.set(Date.now() + normalizedExpiry);
  }
}
